import type { VariantProps } from 'class-variance-authority';

import * as React from 'react';
import { cva } from 'class-variance-authority';
import { ToggleGroup as ToggleGroupPrimitive } from 'radix-ui';

import { cn } from '@shared/utils/cn';

// ToggleButtonGroup Minimal : conteneur bordé, padding/gap 4px, rayon 8px ;
// hauteur totale small 40 / medium 48 / large 56 (item = hauteur - 10px)
const toggleGroupItemVariants = cva(
  "inline-flex flex-1 items-center justify-center gap-2 rounded-md px-3 text-sm font-semibold whitespace-nowrap text-foreground transition-colors outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:hover:bg-primary/90 [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      size: {
        sm: 'min-h-[30px]',
        default: 'min-h-[38px]',
        lg: 'min-h-[46px]',
      },
    },
    defaultVariants: { size: 'default' },
  }
);

const ToggleGroupContext = React.createContext<VariantProps<typeof toggleGroupItemVariants>>({ size: 'default' });

const ToggleGroup = ({
  className,
  size,
  children,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Root> & VariantProps<typeof toggleGroupItemVariants>) => {
  const value = React.useMemo(() => ({ size }), [size]);

  return (
    <ToggleGroupPrimitive.Root
      className={cn('flex w-full items-stretch gap-1 rounded-lg border border-border p-1', className)}
      data-slot="toggle-group"
      {...props}
    >
      <ToggleGroupContext.Provider value={value}>{children}</ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  );
};

const ToggleGroupItem = ({
  className,
  size,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item> & VariantProps<typeof toggleGroupItemVariants>) => {
  const context = React.useContext(ToggleGroupContext);

  return (
    <ToggleGroupPrimitive.Item
      className={cn(toggleGroupItemVariants({ size: size ?? context.size }), className)}
      data-slot="toggle-group-item"
      {...props}
    />
  );
};

export { ToggleGroup, ToggleGroupItem };
